import React, { memo } from 'react'
import styles from './EditApplianceInfo.module.scss'
import { useForm } from 'react-hook-form'
import CommonIncrementInput from '../../../../../../../../common/components/increment-input/CommonIncrementInput'
import { ApplianceItem } from '../../../../../../../../model/solar-recommendation-form/appliance-item'

type Props = {
    initialAppliance: ApplianceItem
    onEditAppliance: (appliance: ApplianceItem) => void
    onSaveChanges: (appliance: ApplianceItem) => void
}

type EditApplianceProps = {
    quantity: number
    dailyUsage: number
}

const EditApplianceInfo: React.FC<Props> = memo((props: Props) => {
    const { initialAppliance, onSaveChanges, onEditAppliance } = props

    const {
        register,
        watch,
        setValue,
        formState: { isValid },
    } = useForm<EditApplianceProps>({
        defaultValues: {
            quantity: initialAppliance.editingProps.quantity!,
            dailyUsage: initialAppliance.editingProps.dailyUsage!,
        },
        reValidateMode: 'onSubmit',
        mode: 'onTouched'
    })

    const applianceForView = initialAppliance.editingProps.newAppliance ? initialAppliance.editingProps.newAppliance : initialAppliance.appliance

    function cancelChanges() {
        onSaveChanges({
            ...initialAppliance,
            editingProps: {
                editing: false,
                newAppliance: undefined,
                dailyUsage: undefined,
                quantity: undefined,
            }
        })
    }

    function saveChanges() {
        onSaveChanges({
            ...initialAppliance,
            editingProps: {
                ...initialAppliance.editingProps,
                dailyUsage: watch('dailyUsage'),
                quantity: watch('quantity'),
            }
        })
    }

    function changeQuantity(quantity: number) {
        if (quantity < 1) {
            quantity = 1
        }

        setValue('quantity', quantity)
    }

    function changeDailyUsage(dailyUsage: number) {
        if (dailyUsage < 1) {
            dailyUsage = 1
        }
        if (dailyUsage > 24) {
            dailyUsage = 24
        }

        setValue('dailyUsage', dailyUsage)
    }

    function editAppliance() {
        onEditAppliance({
            ...initialAppliance,
            editingProps: {
                ...initialAppliance.editingProps,
                editing: true,
                quantity: watch('quantity'),
                dailyUsage: watch('dailyUsage'),
            }

        })
    }


    return (
        <>
            <div className={styles.header}>
                <div className={styles.applianceContainer}>
                    <img src={applianceForView.iconUrl} alt={applianceForView.name} />
                    {applianceForView.name}
                </div>
                <button className={`standardButton ${styles.changeApplianceButton}`} onClick={editAppliance}>Change this appliance <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.74995 8.06942C1.74995 8.85071 1.89785 9.61029 2.19365 10.3482C2.48943 11.086 2.95051 11.7675 3.57687 12.3925L4.21152 13.0272V11.0387C4.21152 10.8262 4.28343 10.648 4.42725 10.5043C4.57105 10.3605 4.74925 10.2887 4.96185 10.2887C5.17443 10.2887 5.35253 10.3605 5.49613 10.5043C5.63971 10.648 5.7115 10.8262 5.7115 11.0387V14.7694C5.7115 15.0255 5.62488 15.2402 5.45165 15.4134C5.2784 15.5866 5.06373 15.6732 4.80765 15.6732H1.0769C0.8644 15.6732 0.686283 15.6013 0.54255 15.4575C0.3988 15.3137 0.326925 15.1355 0.326925 14.9229C0.326925 14.7103 0.3988 14.5323 0.54255 14.3887C0.686283 14.2451 0.8644 14.1733 1.0769 14.1733H3.25L2.52308 13.4771C1.70128 12.7168 1.11697 11.8652 0.770175 10.9223C0.423391 9.97932 0.25 9.02837 0.25 8.06942C0.25 6.56046 0.637175 5.18611 1.41153 3.94637C2.18589 2.70662 3.22563 1.75149 4.53075 1.08097C4.7128 0.979707 4.90414 0.968173 5.10477 1.04637C5.30541 1.12457 5.43777 1.26391 5.50187 1.4644C5.56597 1.65236 5.56207 1.84225 5.49015 2.03405C5.41823 2.22585 5.29073 2.37289 5.10765 2.47517C4.0833 3.02774 3.26759 3.79987 2.66052 4.79157C2.05347 5.78327 1.74995 6.87589 1.74995 8.06942ZM14.25 7.93097C14.25 7.14969 14.1021 6.39011 13.8063 5.65222C13.5105 4.91436 13.0494 4.23291 12.4231 3.60787L11.7884 2.97322V4.96172C11.7884 5.17422 11.7165 5.35235 11.5727 5.4961C11.4289 5.63985 11.2507 5.71172 11.0381 5.71172C10.8255 5.71172 10.6474 5.63985 10.5038 5.4961C10.3602 5.35235 10.2885 5.17422 10.2885 4.96172V1.23097C10.2885 0.97489 10.3751 0.760231 10.5483 0.586998C10.7215 0.413765 10.9362 0.327148 11.1923 0.327148H14.923C15.1355 0.327148 15.3137 0.399056 15.4574 0.542873C15.6012 0.686673 15.673 0.864873 15.673 1.07747C15.673 1.29006 15.6012 1.46814 15.4574 1.61172C15.3137 1.75531 15.1355 1.8271 14.923 1.8271H12.75L13.4769 2.5233C14.2668 3.31315 14.8431 4.17151 15.2059 5.09837C15.5686 6.02524 15.75 6.96994 15.75 7.93247C15.75 9.44044 15.3644 10.8127 14.5932 12.0492C13.8221 13.2858 12.7839 14.2393 11.4788 14.9098C11.2968 15.0111 11.1054 15.0242 10.9048 14.9492C10.7041 14.8742 10.5718 14.7365 10.5077 14.536C10.4436 14.348 10.4477 14.1579 10.5202 13.9656C10.5926 13.7733 10.7167 13.6265 10.8923 13.5252C11.9166 12.9727 12.7324 12.2005 13.3394 11.2088C13.9465 10.2171 14.25 9.12451 14.25 7.93097Z" fill="var(--app-primary-text)" />
                </svg>
                </button>
            </div>
            <div className={styles.propertiesContainer}>
                <div className={styles.propertyContainer}>
                    <div className={styles.propertyInfoContainer}>
                        <div className={styles.propertyName}>Quantity</div>
                        <div className={styles.propertyDescription}>The number of units to be used</div>
                    </div>
                    <CommonIncrementInput
                        value={watch('quantity')}
                        min={1}
                        inputProps={register('quantity', {
                            required: 'Quantity is required',
                            min: {
                                value: 1,
                                message: 'Quantity must be at least 1',
                            },
                        })}
                        onChange={changeQuantity} />
                </div>
                <div className={styles.propertyContainer}>
                    <div className={styles.propertyInfoContainer}>
                        <div className={styles.propertyName}>Daily usage (Hrs)</div>
                        <div className={styles.propertyDescription}>How long will this device be in use</div>
                    </div>
                    <CommonIncrementInput
                        value={watch('dailyUsage')}
                        min={1}
                        max={24}
                        unit='hrs'
                        inputProps={register('dailyUsage', {
                            required: 'Daily usage is required',
                            min: {
                                value: 1,
                                message: 'Daily usage must be at least 1',
                            },
                            max: {
                                value: 24,
                                message: 'Daily usage must be at most 24',
                            },
                        })}
                        onChange={changeDailyUsage} />
                </div>
            </div>
            <div className={styles.buttonContainer}>
                <button className={`standardButton ${styles.cancelButton}`} onClick={cancelChanges} >Cancel</button>
                <button className={`standardButton ${isValid ? styles.saveButton : styles.invalidSaveButton}`} onClick={isValid ? saveChanges : undefined} >Save Changes </button>
            </div>
        </>
    )
})

export default EditApplianceInfo